import { useEffect, useState } from 'react';
import type { CookDetails, MeatProperties } from '../../types';

interface Props {
  data: Partial<CookDetails>;
  onChange: (data: Partial<CookDetails>) => void;
  onNext: () => void;
}

export default function MeatSelect({ data: _data, onChange, onNext }: Props) {
  const [meats, setMeats] = useState<MeatProperties[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load meat types from config
    fetch('/config/meats.json')
      .then((res) => res.json())
      .then((data) => {
        setMeats(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load meats:', err);
        setLoading(false);
      });
  }, []);

  const handleSelect = (meatType: string, variant?: string) => {
    onChange({ meatType, variant });
    setTimeout(onNext, 200);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-flame-600" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {meats.map((meat) => (
        <div key={meat.type} className="border-2 border-smoke-200 rounded-lg overflow-hidden">
          {/* Main button - clickable only if no variants */}
          {!meat.variants ? (
            <button
              onClick={() => handleSelect(meat.type)}
              className="w-full text-left p-4 hover:bg-flame-50 transition-colors"
            >
              <div className="font-semibold text-smoke-900">{meat.displayName}</div>
              <div className="text-sm text-smoke-600 mt-1">
                {meat.temperatures.targetInternalF}°F · {
                  meat.empirical.useFixedCookTime && meat.empirical.fixedBaseTimeAt250F
                    ? `~${meat.empirical.fixedBaseTimeAt250F} hrs @ 250°F`
                    : `${meat.empirical.baseCookRateHrsPerLb225F} hrs/lb @ 225°F`
                }
              </div>
            </button>
          ) : (
            <div className="p-4 bg-smoke-50">
              <div className="font-semibold text-smoke-900">{meat.displayName}</div>
              <div className="text-sm text-smoke-600 mt-1">
                Choose the type you're cooking below
              </div>
            </div>
          )}

          {/* Variants */}
          {meat.variants && (
            <div className="border-t border-smoke-200 bg-white p-3 space-y-2">
              {Object.entries(meat.variants).map(([key, variant]) => {
                const targetTemp = variant.targetInternalF || meat.temperatures.targetInternalF;
                const baseRate = meat.empirical.baseCookRateHrsPerLb225F;
                const adjustedRate = variant.cookRateMultiplier ? 
                  (baseRate * variant.cookRateMultiplier).toFixed(1) : baseRate;
                const fixedTime = meat.empirical.useFixedCookTime && meat.empirical.fixedBaseTimeAt250F;
                const adjustedFixedTime = fixedTime && variant.cookRateMultiplier ?
                  (meat.empirical.fixedBaseTimeAt250F! * variant.cookRateMultiplier).toFixed(1) :
                  meat.empirical.fixedBaseTimeAt250F;
                
                return (
                  <button
                    key={key}
                    onClick={() => handleSelect(meat.type, key)}
                    className="w-full text-left px-3 py-2 bg-white rounded border-2 border-smoke-200 hover:border-flame-500 hover:bg-flame-50 transition-colors"
                  >
                    <div className="font-medium text-smoke-900">{variant.displayName}</div>
                    <div className="text-xs text-smoke-600 mt-1">
                      {targetTemp}°F · {
                        fixedTime
                          ? `~${adjustedFixedTime} hrs @ 250°F`
                          : `${adjustedRate} hrs/lb @ 225°F`
                      }
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
